import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { usePublicPageSection } from "../../../../hooks/usePublicPageSection";
import { submitCase } from "../../../../api/casesApi";
import { resolveMediaUrl } from "../../../../utils/mediaUrl";
import { getApiErrorMessage } from "../../../../utils/apiResponse";
import { useLanguage } from "../../../../context/LanguageContext";
import "./ReportCorruption.css";

const requireField = (fields, key) => {
  const value = fields?.[key];
  if (value == null || String(value).trim() === "") {
    throw new Error(`Report form field "${key}" is missing from the API response.`);
  }
  return value;
};

const requireMediaUrl = (section, groupKey) => {
  const item = section.media?.groups?.[groupKey]?.[0] || null;
  if (!item?.url) {
    throw new Error(`Report form media group "${groupKey}" is missing from the API response.`);
  }
  return resolveMediaUrl(item.url);
};

const buildModel = (section) => {
  const f = section.fields || {};
  return {
    step1Number: requireField(f, "step1Number"),
    step1Title: requireField(f, "step1Title"),
    step1Subtitle: requireField(f, "step1Subtitle"),
    subjectLabel: requireField(f, "subjectLabel"),
    subjectPlaceholder: requireField(f, "subjectPlaceholder"),
    descriptionLabel: requireField(f, "descriptionLabel"),
    descriptionPlaceholder: requireField(f, "descriptionPlaceholder"),
    descriptionHint: requireField(f, "descriptionHint"),

    step2Number: requireField(f, "step2Number"),
    step2Title: requireField(f, "step2Title"),
    step2Subtitle: requireField(f, "step2Subtitle"),
    categoryLabel: requireField(f, "categoryLabel"),
    institutionLabel: requireField(f, "institutionLabel"),
    locationLabel: requireField(f, "locationLabel"),
    dateLabel: requireField(f, "dateLabel"),
    selectPlaceholder: requireField(f, "selectPlaceholder"),
    datePlaceholder: requireField(f, "datePlaceholder"),
    dateAriaLabel: requireField(f, "dateAriaLabel"),
    categories: [1, 2, 3, 4, 5].map((n) => requireField(f, `category${n}`)),
    institutions: [1, 2, 3, 4, 5].map((n) => requireField(f, `institution${n}`)),
    locations: [1, 2, 3, 4, 5, 6].map((n) => requireField(f, `location${n}`)),

    step3Number: requireField(f, "step3Number"),
    step3Title: requireField(f, "step3Title"),
    step3Subtitle: requireField(f, "step3Subtitle"),
    attachmentsLabel: requireField(f, "attachmentsLabel"),
    uploadTextLead: requireField(f, "uploadTextLead"),
    uploadTextRest: requireField(f, "uploadTextRest"),
    uploadHint: requireField(f, "uploadHint"),
    metaNote: requireField(f, "metaNote"),

    step4Number: requireField(f, "step4Number"),
    step4Title: requireField(f, "step4Title"),
    step4Subtitle: requireField(f, "step4Subtitle"),
    confidentialNote: requireField(f, "confidentialNote"),
    fullNameLabel: requireField(f, "fullNameLabel"),
    fullNamePlaceholder: requireField(f, "fullNamePlaceholder"),
    nationalIdLabel: requireField(f, "nationalIdLabel"),
    nationalIdPlaceholder: requireField(f, "nationalIdPlaceholder"),
    emailLabel: requireField(f, "emailLabel"),
    emailPlaceholder: requireField(f, "emailPlaceholder"),
    phoneLabel: requireField(f, "phoneLabel"),
    phoneOptional: requireField(f, "phoneOptional"),
    phonePlaceholder: requireField(f, "phonePlaceholder"),
    phoneCountryCode: requireField(f, "phoneCountryCode"),
    phoneCountryAria: requireField(f, "phoneCountryAria"),

    step5Number: requireField(f, "step5Number"),
    step5Title: requireField(f, "step5Title"),
    step5Subtitle: requireField(f, "step5Subtitle"),
    confirmText: requireField(f, "confirmText"),
    recaptchaPlaceholder: requireField(f, "recaptchaPlaceholder"),
    submitButton: requireField(f, "submitButton"),
    submitNote: requireField(f, "submitNote"),

    howTitle: requireField(f, "howTitle"),
    howItWorks: [1, 2, 3].map((n) => ({
      step: requireField(f, `how${n}Step`),
      title: requireField(f, `how${n}Title`),
      text: requireField(f, `how${n}Text`),
    })),
    protectedTitle: requireField(f, "protectedTitle"),
    protections: [1, 2, 3, 4].map((n) => ({
      label: requireField(f, `prot${n}Label`),
      icon: requireMediaUrl(section, `prot-${n}`),
    })),

    calendarIcon: requireMediaUrl(section, "calendar"),
    uploadIcon: requireMediaUrl(section, "upload"),
    eyeOffIcon: requireMediaUrl(section, "eye-off"),
    shieldIcon: requireMediaUrl(section, "shield"),
  };
};

const IraqFlag = () => (
  <svg
    className="report-form__flag"
    width="20"
    height="14"
    viewBox="0 0 20 14"
    fill="none"
    aria-hidden="true"
  >
    <rect width="20" height="4.67" fill="#CE1126" />
    <rect y="4.67" width="20" height="4.66" fill="#FFFFFF" />
    <rect y="9.33" width="20" height="4.67" fill="#000000" />
    <rect
      x="0.25"
      y="0.25"
      width="19.5"
      height="13.5"
      rx="1"
      stroke="#D5D7DA"
      strokeWidth="0.5"
      fill="none"
    />
  </svg>
);

const StepHeader = ({ number, title, subtitle }) => (
  <div className="report-form__step-header">
    <span className="report-form__step-number">{number}</span>
    <div className="report-form__step-copy">
      <h2 className="report-form__step-title">{title}</h2>
      <p className="report-form__step-subtitle">{subtitle}</p>
    </div>
  </div>
);

const emptyForm = {
  subject: "",
  description: "",
  category: "",
  institution: "",
  location: "",
  date: "",
  fullName: "",
  nationalId: "",
  email: "",
  phone: "",
  confirmed: false,
};

const ReportCorruption = () => {
  const navigate = useNavigate();
  const { language, meta } = useLanguage();
  const fileInputRef = useRef(null);
  const { model, error, isLoading } = usePublicPageSection("report", "form", buildModel);
  const [form, setForm] = useState(emptyForm);
  const [files, setFiles] = useState([]);
  const [dragging, setDragging] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  useEffect(() => {
    if (model) {
      setForm(emptyForm);
      setFiles([]);
    }
  }, [language, model]);

  const updateField = (key) => (event) => {
    const value =
      event.target.type === "checkbox"
        ? event.target.checked
        : event.target.value;
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleFiles = (fileList) => {
    const next = Array.from(fileList || []);
    if (!next.length) return;
    setFiles((prev) => [...prev, ...next]);
  };

  const onDrop = (event) => {
    event.preventDefault();
    setDragging(false);
    handleFiles(event.dataTransfer.files);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (submitting) return;

    if (!form.confirmed) {
      setSubmitError(
        language === "ar"
          ? "يرجى تأكيد صحة المعلومات."
          : language === "ku"
            ? "تکایە دڵنیابوونەوە لە دروستی زانیارییەکان."
            : "Please confirm the information is accurate."
      );
      return;
    }

    setSubmitting(true);
    setSubmitError("");
    try {
      const result = await submitCase({
        subject: form.subject,
        description: form.description,
        category: form.category,
        institution: form.institution,
        location: form.location,
        date: form.date || undefined,
        fullName: form.fullName,
        nationalId: form.nationalId,
        email: form.email,
        phone: form.phone || undefined,
        confirmed: form.confirmed,
        files,
      });
      navigate("/report/submitted", {
        state: { caseId: result.caseId },
        replace: true,
      });
    } catch (err) {
      setSubmitError(getApiErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <section className="report-form" dir={meta.dir}>
        <div className="report-form__container">
          <p style={{ color: "#64748b", fontSize: 14 }}>Loading…</p>
        </div>
      </section>
    );
  }

  if (error || !model) {
    return (
      <section className="report-form" dir={meta.dir}>
        <div className="report-form__container">
          <p style={{ color: "#b91c1c", fontSize: 14 }}>
            {error || "Report form unavailable."}
          </p>
        </div>
      </section>
    );
  }

  return (
    <section className="report-form" dir={meta.dir}>
      <div className="report-form__container">
        <form className="report-form__main" onSubmit={handleSubmit}>
          <div className="report-form__card">
            <StepHeader
              number={model.step1Number}
              title={model.step1Title}
              subtitle={model.step1Subtitle}
            />

            <div className="report-form__fields">
              <label className="report-form__field">
                <span className="report-form__label">{model.subjectLabel}</span>
                <input
                  type="text"
                  className="report-form__input"
                  placeholder={model.subjectPlaceholder}
                  value={form.subject}
                  onChange={updateField("subject")}
                />
              </label>

              <label className="report-form__field">
                <span className="report-form__label">{model.descriptionLabel}</span>
                <textarea
                  className="report-form__textarea"
                  placeholder={model.descriptionPlaceholder}
                  rows={5}
                  value={form.description}
                  onChange={updateField("description")}
                />
                <span className="report-form__hint">{model.descriptionHint}</span>
              </label>
            </div>
          </div>

          <div className="report-form__card">
            <StepHeader
              number={model.step2Number}
              title={model.step2Title}
              subtitle={model.step2Subtitle}
            />

            <div className="report-form__grid">
              <label className="report-form__field">
                <span className="report-form__label">{model.categoryLabel}</span>
                <div className="report-form__select-wrap">
                  <select
                    className={`report-form__select ${
                      !form.category ? "report-form__select--placeholder" : ""
                    }`}
                    value={form.category}
                    onChange={updateField("category")}
                  >
                    <option value="">{model.selectPlaceholder}</option>
                    {model.categories.map((item) => (
                      <option key={item} value={item}>
                        {item}
                      </option>
                    ))}
                  </select>
                </div>
              </label>

              <label className="report-form__field">
                <span className="report-form__label">{model.institutionLabel}</span>
                <div className="report-form__select-wrap">
                  <select
                    className={`report-form__select ${
                      !form.institution
                        ? "report-form__select--placeholder"
                        : ""
                    }`}
                    value={form.institution}
                    onChange={updateField("institution")}
                  >
                    <option value="">{model.selectPlaceholder}</option>
                    {model.institutions.map((item) => (
                      <option key={item} value={item}>
                        {item}
                      </option>
                    ))}
                  </select>
                </div>
              </label>

              <label className="report-form__field">
                <span className="report-form__label">{model.locationLabel}</span>
                <div className="report-form__select-wrap">
                  <select
                    className={`report-form__select ${
                      !form.location ? "report-form__select--placeholder" : ""
                    }`}
                    value={form.location}
                    onChange={updateField("location")}
                  >
                    <option value="">{model.selectPlaceholder}</option>
                    {model.locations.map((item) => (
                      <option key={item} value={item}>
                        {item}
                      </option>
                    ))}
                  </select>
                </div>
              </label>

              <label className="report-form__field">
                <span className="report-form__label">{model.dateLabel}</span>
                <div className="report-form__date-wrap">
                  <img
                    src={model.calendarIcon}
                    alt=""
                    className="report-form__date-icon"
                    aria-hidden="true"
                  />
                  <input
                    type="date"
                    className={`report-form__input report-form__input--date ${
                      !form.date ? "report-form__input--empty" : ""
                    }`}
                    value={form.date}
                    onChange={updateField("date")}
                    aria-label={model.dateAriaLabel}
                  />
                  {!form.date && (
                    <span className="report-form__date-placeholder">
                      {model.datePlaceholder}
                    </span>
                  )}
                </div>
              </label>
            </div>
          </div>

          <div className="report-form__card">
            <StepHeader
              number={model.step3Number}
              title={model.step3Title}
              subtitle={model.step3Subtitle}
            />

            <div className="report-form__fields">
              <div className="report-form__field">
                <span className="report-form__label">{model.attachmentsLabel}</span>
                <div
                  className={`report-form__upload ${
                    dragging ? "report-form__upload--dragging" : ""
                  }`}
                  onDragOver={(event) => {
                    event.preventDefault();
                    setDragging(true);
                  }}
                  onDragLeave={() => setDragging(false)}
                  onDrop={onDrop}
                  onClick={() => fileInputRef.current?.click()}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      fileInputRef.current?.click();
                    }
                  }}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    className="report-form__file-input"
                    multiple
                    accept=".pdf,image/*,.doc,.docx,.xls,.xlsx,.txt"
                    onChange={(event) => handleFiles(event.target.files)}
                  />
                  <img
                    src={model.uploadIcon}
                    alt=""
                    className="report-form__upload-icon"
                    aria-hidden="true"
                  />
                  <p className="report-form__upload-text">
                    <span>{model.uploadTextLead}</span> {model.uploadTextRest}
                  </p>
                  <p className="report-form__upload-hint">{model.uploadHint}</p>
                </div>

                {files.length > 0 && (
                  <ul className="report-form__file-list">
                    {files.map((file) => (
                      <li key={`${file.name}-${file.lastModified}`}>
                        {file.name}
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <p className="report-form__meta-note">
                <img src={model.eyeOffIcon} alt="" aria-hidden="true" />
                <span>{model.metaNote}</span>
              </p>
            </div>
          </div>

          <div className="report-form__card">
            <StepHeader
              number={model.step4Number}
              title={model.step4Title}
              subtitle={model.step4Subtitle}
            />

            <div className="report-form__confidential">
              <img src={model.shieldIcon} alt="" aria-hidden="true" />
              <span>{model.confidentialNote}</span>
            </div>

            <div className="report-form__grid">
              <label className="report-form__field">
                <span className="report-form__label">{model.fullNameLabel}</span>
                <input
                  type="text"
                  className="report-form__input"
                  placeholder={model.fullNamePlaceholder}
                  value={form.fullName}
                  onChange={updateField("fullName")}
                />
              </label>

              <label className="report-form__field">
                <span className="report-form__label">{model.nationalIdLabel}</span>
                <input
                  type="text"
                  className="report-form__input"
                  placeholder={model.nationalIdPlaceholder}
                  value={form.nationalId}
                  onChange={updateField("nationalId")}
                />
              </label>

              <label className="report-form__field">
                <span className="report-form__label">{model.emailLabel}</span>
                <input
                  type="email"
                  className="report-form__input"
                  placeholder={model.emailPlaceholder}
                  value={form.email}
                  onChange={updateField("email")}
                />
              </label>

              <label className="report-form__field">
                <span className="report-form__label">
                  {model.phoneLabel}{" "}
                  <span className="report-form__optional">
                    {model.phoneOptional}
                  </span>
                </span>
                <div className="report-form__phone">
                  <button
                    type="button"
                    className="report-form__phone-country"
                    aria-label={model.phoneCountryAria}
                  >
                    <IraqFlag />
                    <span>{model.phoneCountryCode}</span>
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 12 12"
                      fill="none"
                      aria-hidden="true"
                    >
                      <path
                        d="M3 4.5L6 7.5L9 4.5"
                        stroke="#717680"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                  <input
                    type="tel"
                    className="report-form__input report-form__input--phone"
                    placeholder={model.phonePlaceholder}
                    value={form.phone}
                    onChange={updateField("phone")}
                  />
                </div>
              </label>
            </div>
          </div>

          <div className="report-form__card">
            <StepHeader
              number={model.step5Number}
              title={model.step5Title}
              subtitle={model.step5Subtitle}
            />

            <label className="report-form__confirm">
              <input
                type="checkbox"
                checked={form.confirmed}
                onChange={updateField("confirmed")}
              />
              <span>{model.confirmText}</span>
            </label>

            <div className="report-form__recaptcha" aria-hidden="true">
              {model.recaptchaPlaceholder}
            </div>

            <div className="report-form__submit-row">
              {submitError && (
                <p style={{ color: "#b91c1c", fontSize: 14, marginBottom: 12 }}>
                  {submitError}
                </p>
              )}
              <button
                type="submit"
                className="report-form__submit"
                disabled={submitting}
              >
                {submitting ? "…" : model.submitButton}
              </button>
              <p className="report-form__submit-note">{model.submitNote}</p>
            </div>
          </div>
        </form>

        <aside className="report-form__sidebar">
          <div className="report-form__sidebar-inner">
            <div className="report-form__how">
              <h3 className="report-form__sidebar-title">{model.howTitle}</h3>
              <ol className="report-form__timeline">
                {model.howItWorks.map((item) => (
                  <li key={item.step} className="report-form__timeline-item">
                    <span className="report-form__timeline-number">
                      {item.step}
                    </span>
                    <div className="report-form__timeline-copy">
                      <p className="report-form__timeline-title">{item.title}</p>
                      <p className="report-form__timeline-text">{item.text}</p>
                    </div>
                  </li>
                ))}
              </ol>
            </div>

            <div className="report-form__protected">
              <h3 className="report-form__sidebar-title">
                {model.protectedTitle}
              </h3>
              <ul className="report-form__protection-list">
                {model.protections.map((item) => (
                  <li key={item.label} className="report-form__protection-item">
                    <span className="report-form__protection-icon">
                      <img src={item.icon} alt="" aria-hidden="true" />
                    </span>
                    <span>{item.label}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </aside>
      </div>
    </section>
  );
};

export default ReportCorruption;
